/* eslint-disable */
import { ServerGroupStatus } from "../common/server_group_status";
import { ServerGroupType } from "../common/server_group_type";

export const protobufPackage = "tecton_proto.servergroupservice";

/** Operational status of Server Groups indicating their current state and availability. */
export enum Status {
  /** UNSPECIFIED - Status not specified or unknown. */
  UNSPECIFIED = "UNSPECIFIED",
  /** READY - Server Group is fully operational and ready to process requests. */
  READY = "READY",
  /** PENDING - Server Group creation has been requested but not yet started. */
  PENDING = "PENDING",
  /**
   * CREATING - Server Group is currently being created and is not operational yet.
   * Will transition to READY or ERROR when creation completes.
   */
  CREATING = "CREATING",
  /**
   * UPDATING - Server Group is being updated but remains operational. Additional updates are allowed.
   * Will transition to READY or ERROR when update completes.
   */
  UPDATING = "UPDATING",
  /**
   * DELETING - Server Group deletion is in progress and may not be fully operational.
   * Will transition to ERROR if deletion fails or be removed if successful.
   */
  DELETING = "DELETING",
  /** ERROR - Server Group is not operational and requires intervention to resolve issues. */
  ERROR = "ERROR",
}

export interface ListServerGroupsRequest {
  workspace?: string | undefined;
  type?: ServerGroupType | undefined;
}

export interface ListServerGroupsResponse {
  server_groups?: ServerGroupInfo[] | undefined;
}

export interface GetServerGroupRequest {
  workspace?: string | undefined;
  server_group_name?: string | undefined;
}

export interface GetServerGroupResponse {
  server_group?: ServerGroupInfo | undefined;
}

export interface ServerGroupInfo {
  server_group_id?: string | undefined;
  name?: string | undefined;
  type?: ServerGroupType | undefined;
  description?: string | undefined;
  created_at?: string | undefined;
  owner?: string | undefined;
  last_modified_by?: string | undefined;
  tags?: { [key: string]: string } | undefined;
  desired_config?: ServerGroupScalingConfig | undefined;
  current_config?: ServerGroupScalingConfig | undefined;
  status?: ServerGroupStatus | undefined;
  status_details?: string | undefined;
  environment?: string | undefined;
}

export interface ServerGroupInfo_TagsEntry {
  key: string;
  value: string;
}

export interface ServerGroupScalingConfig {
  min_nodes?: number | undefined;
  max_nodes?: number | undefined;
  desired_nodes?: number | undefined;
  autoscaling_enabled?: boolean | undefined;
  last_updated_at?: string | undefined;
  workspace_state_id?: string | undefined;
}

export interface GetRealtimeLogsRequest {
  transform_server_group_id?: string | undefined;
  start?: string | undefined;
  end?: string | undefined;
  tail_log_count?: number | undefined;
}

export interface GetRealtimeLogsResponse {
  logs?: RealtimeLog[] | undefined;
  warnings?: string | undefined;
}

export interface RealtimeLog {
  timestamp?: string | undefined;
  message?: string | undefined;
  node?: string | undefined;
}

export interface TestOnlyUpdateServerStateRequest {
  workspace?: string | undefined;
  server_group_name?: string | undefined;
}

export interface TestOnlyUpdateServerStateResponse {
}

export interface TestOnlyDeleteAllServerStatesRequest {
  workspace?: string | undefined;
}

export interface TestOnlyDeleteAllServerStatesResponse {
}

export interface TestOnlyCreateTransformServerGroupRequest {
  workspace?: string | undefined;
  name?: string | undefined;
  environment?: string | undefined;
  desired_nodes?: number | undefined;
}

export interface TestOnlyCreateTransformServerGroupResponse {
  transform_server_group?: TransformServerGroup | undefined;
}

export interface TestOnlyCreateIngestServerGroupRequest {
  workspace?: string | undefined;
  name?: string | undefined;
  desired_nodes?: number | undefined;
}

export interface TestOnlyCreateIngestServerGroupResponse {
  ingest_server_group?: IngestServerGroup | undefined;
}

/**
 * Metadata for Server Groups containing descriptive information and organizational labels.
 * This metadata helps with Server Group organization and ownership tracking across environments.
 */
export interface ResourceMetadata {
  /** Human-readable description of the Server Group. */
  description?:
    | string
    | undefined;
  /**
   * Key-value pairs for categorizing and organizing Server Groups.
   * Examples include environment (prod/staging/dev), team, and cost center.
   * Restrictions: Maximum 25 tags. Keys must be ≤128 characters. Values must be ≤256 characters. Keys cannot start with 'aws'.
   * Note: For Update operations, providing tags will completely replace all existing tags (not merge with existing tags).
   */
  tags?:
    | { [key: string]: string }
    | undefined;
  /** Email address or identifier of the individual or team responsible for this Server Group. */
  owner?: string | undefined;
}

export interface ResourceMetadata_TagsEntry {
  key: string;
  value: string;
}

/**
 * Autoscaling configuration for Server Groups with dynamic scaling.
 * Specifies the node count range that Tecton can scale between based on workload.
 */
export interface AutoscalingConfig {
  /** Specifies the minimum number of nodes to maintain in the Server Group. Must be ≥0 and ≤`max_nodes`. */
  min_nodes?:
    | number
    | undefined;
  /** Specifies the maximum number of nodes allowed in the Server Group. Must be ≥0, ≥`min_nodes`, and ≤configured max for your deployment (default 50). */
  max_nodes?: number | undefined;
}

/**
 * Provisioned scaling configuration for Server Groups with fixed capacity.
 * Specifies the constant node count for the Server Group.
 */
export interface ProvisionedScalingConfig {
  /** Specifies the desired fixed number of nodes to maintain in the Server Group. Must be ≥0. */
  desired_nodes?: number | undefined;
}

export interface TransformServerGroup {
  /** The name of the workspace containing this Transform Server Group. */
  workspace?:
    | string
    | undefined;
  /** The name of the Transform Server Group. */
  name?:
    | string
    | undefined;
  /** The ID of the Transform Server Group. */
  id?:
    | string
    | undefined;
  /** Metadata containing descriptive information and organizational labels for the Transform Server Group. Optional. */
  metadata?:
    | ResourceMetadata
    | undefined;
  /** Autoscaling configuration for the Transform Server Group. Present if the Server Group has autoscaling enabled. */
  autoscaling_config?:
    | AutoscalingConfig
    | undefined;
  /** Provisioned scaling configuration for the Transform Server Group. Present if the Server Group has provisioned scaling enabled. */
  provisioned_config?:
    | ProvisionedScalingConfig
    | undefined;
  /** The current operational status of the Transform Server Group. */
  status?:
    | Status
    | undefined;
  /** Additional details about the current status of the Transform Server Group. Optional. */
  status_details?:
    | string
    | undefined;
  /** The instance type for Transform Server Group nodes. Must be a valid instance type (e.g., m5.large, c5.xlarge). */
  node_type?:
    | string
    | undefined;
  /** The Tecton Environment used for executing transformations in this Transform Server Group. */
  environment?:
    | string
    | undefined;
  /**
   * Key-value pairs of environment variables for the Transform Server Group.
   * These variables will be available in the runtime environment where user transformations are executed.
   */
  environment_variables?:
    | { [key: string]: string }
    | undefined;
  /** Timestamp when the Transform Server Group was created. */
  created_at?:
    | string
    | undefined;
  /** Timestamp when the Transform Server Group was last updated. */
  updated_at?:
    | string
    | undefined;
  /** Configuration changes requested but not yet applied to the Transform Server Group. */
  pending_config?: TransformServerGroup_PendingConfig | undefined;
}

export interface TransformServerGroup_EnvironmentVariablesEntry {
  key: string;
  value: string;
}

/**
 * Configuration changes requested but not yet applied to the Transform Server Group.
 * Present only when the requested configuration differs from the current state.
 */
export interface TransformServerGroup_PendingConfig {
  /** Autoscaling configuration being applied to the Transform Server Group. */
  autoscaling_config?:
    | AutoscalingConfig
    | undefined;
  /** Provisioned scaling configuration being applied to the Transform Server Group. */
  provisioned_config?:
    | ProvisionedScalingConfig
    | undefined;
  /** The Tecton Environment for executing transformations. */
  environment?:
    | string
    | undefined;
  /** Environment variables being applied to the Transform Server Group. */
  environment_variables?:
    | { [key: string]: string }
    | undefined;
  /** Instance type being applied to the Transform Server Group. */
  node_type?: string | undefined;
}

export interface TransformServerGroup_PendingConfig_EnvironmentVariablesEntry {
  key: string;
  value: string;
}

export interface CreateTransformServerGroupRequest {
  /** The name of the workspace to create the Transform Server Group in. Must be a live workspace. */
  workspace?:
    | string
    | undefined;
  /**
   * The name of the Transform Server Group. Must be unique within the workspace.
   * Naming requirements: Start with a letter. Use only letters, digits, hyphens, or underscores. Maximum 64 characters.
   * Note: Combined workspace and Server Group name length cannot exceed 64 characters.
   */
  name?: string | undefined;
  metadata?:
    | ResourceMetadata
    | undefined;
  /**
   * Scaling configuration: Exactly one of `autoscaling_config` or `provisioned_config` must be provided (mutually exclusive).
   * Note: We do not use `oneof` in this scenario because the conversion to the proto will silently ignore
   * one scaling config rather than being caught by application.
   */
  autoscaling_config?: AutoscalingConfig | undefined;
  provisioned_config?:
    | ProvisionedScalingConfig
    | undefined;
  /** The instance type for Transform Server Group nodes. Optional. Defaults to `m6a.2xlarge` if not specified. Must be a valid instance type (e.g., m5.large, c5.xlarge). */
  node_type?:
    | string
    | undefined;
  /**
   * The Tecton Environment to use for executing transformations in this Transform Server Group. Required.
   * Specify a Tecton-managed core environment or use a custom environment. Must be READY and include the tecton-runtime package.
   * See https://docs.tecton.ai/docs/defining-features/feature-views/realtime-feature-view/realtime-feature-view-environments for creating custom environments.
   */
  environment?:
    | string
    | undefined;
  /**
   * Key-value pairs of environment variables for the Transform Server Group.
   * These variables will be available in the runtime environment where user transformations are executed.
   */
  environment_variables?: { [key: string]: string } | undefined;
}

export interface CreateTransformServerGroupRequest_EnvironmentVariablesEntry {
  key: string;
  value: string;
}

export interface UpdateTransformServerGroupRequest {
  /** The ID of the Transform Server Group to update. */
  id?:
    | string
    | undefined;
  /** Metadata containing descriptive information and organizational labels for the Transform Server Group. */
  metadata?:
    | ResourceMetadata
    | undefined;
  /** Scaling configuration: Exactly one of `autoscaling_config` or `provisioned_config` must be provided (mutually exclusive). */
  autoscaling_config?: AutoscalingConfig | undefined;
  provisioned_config?:
    | ProvisionedScalingConfig
    | undefined;
  /**
   * The instance type for Transform Server Group nodes. Optional. Defaults to `m6a.2xlarge` if not specified. Must be a valid instance type (e.g., m5.large, c5.xlarge).
   * Note: Node type updates may take several minutes to complete.
   */
  node_type?:
    | string
    | undefined;
  /**
   * The Tecton Environment to use for executing transformations in this Transform Server Group. Required.
   * Specify a Tecton-managed core environment or use a custom environment. Must be READY and include the tecton-runtime package.
   * See https://docs.tecton.ai/docs/defining-features/feature-views/realtime-feature-view/realtime-feature-view-environments for creating custom environments.
   * Note: In-place environment updates should be done with caution, as any misconfiguration could impact a live Transform Server Group. To avoid disruptions, we recommend creating a new Transform Server Group with the desired environment instead.
   */
  environment?:
    | string
    | undefined;
  /**
   * Key-value pairs of environment variables for the Transform Server Group.
   * These variables will be available in the runtime environment where user transformations are executed.
   * Note: For Update operations, providing environment_variables will completely replace all existing environment variables (not merge with existing variables).
   */
  environment_variables?: { [key: string]: string } | undefined;
}

export interface UpdateTransformServerGroupRequest_EnvironmentVariablesEntry {
  key: string;
  value: string;
}

export interface DeleteTransformServerGroupRequest {
  /** The ID of the Transform Server Group to delete. */
  id?: string | undefined;
}

/** empty */
export interface DeleteTransformServerGroupResponse {
}

export interface GetTransformServerGroupRequest {
  /** The ID of the Transform Server Group to retrieve. */
  id?: string | undefined;
}

export interface ListTransformServerGroupsRequest {
  /**
   * The name of the workspace to list Transform Server Groups in.
   * If no workspace is provided, list all Transform Server Groups across all workspaces
   * the caller has permissions to view.
   */
  workspace?: string | undefined;
}

export interface ListTransformServerGroupsResponse {
  /**
   * List of Transform Server Groups in the specified workspace (or all workspaces
   * the caller can view if no workspace was requested)
   */
  transform_server_groups?: TransformServerGroup[] | undefined;
}

export interface IngestServerGroup {
  /** The name of the workspace containing this Ingest Server Group. */
  workspace?:
    | string
    | undefined;
  /** The name of the Ingest Server Group. */
  name?:
    | string
    | undefined;
  /** The ID of the Ingest Server Group. */
  id?:
    | string
    | undefined;
  /** Metadata containing descriptive information and organizational labels for the Ingest Server Group. Optional. */
  metadata?:
    | ResourceMetadata
    | undefined;
  /** The current operational status of the Ingest Server Group. */
  status?:
    | Status
    | undefined;
  /** Additional details about the current status of the Ingest Server Group. Optional. */
  status_details?:
    | string
    | undefined;
  /** Timestamp when the Ingest Server Group was created. */
  created_at?:
    | string
    | undefined;
  /** Timestamp when the Ingest Server Group was last updated. */
  updated_at?:
    | string
    | undefined;
  /** Autoscaling configuration for the Ingest Server Group. Present if the Server Group has autoscaling enabled. */
  autoscaling_config?:
    | AutoscalingConfig
    | undefined;
  /** Provisioned scaling configuration for the Ingest Server Group. Present if the Server Group has provisioned scaling enabled. */
  provisioned_config?:
    | ProvisionedScalingConfig
    | undefined;
  /** The instance type for Ingest Server Group nodes. Must be a valid instance type (e.g., m5.large, c5.xlarge). */
  node_type?:
    | string
    | undefined;
  /** Configuration changes requested but not yet applied to the Ingest Server Group. */
  pending_config?: IngestServerGroup_PendingConfig | undefined;
}

/**
 * Configuration changes requested but not yet applied to the Ingest Server Group.
 * Present only when the requested configuration differs from the current state.
 */
export interface IngestServerGroup_PendingConfig {
  /** Autoscaling configuration being applied to the Ingest Server Group. */
  autoscaling_config?:
    | AutoscalingConfig
    | undefined;
  /** Provisioned scaling configuration being applied to the Ingest Server Group. */
  provisioned_config?:
    | ProvisionedScalingConfig
    | undefined;
  /** Instance type being applied to the Ingest Server Group. */
  node_type?: string | undefined;
}

export interface CreateIngestServerGroupRequest {
  /** The name of the workspace to create the Ingest Server Group in. Must be a live workspace. */
  workspace?:
    | string
    | undefined;
  /**
   * The name of the Ingest Server Group. Must be unique within the workspace.
   * Naming requirements: Start with a letter. Use only letters, digits, hyphens, or underscores. Maximum 64 characters.
   * Note: Combined workspace and Server Group name length cannot exceed 64 characters.
   */
  name?: string | undefined;
  metadata?:
    | ResourceMetadata
    | undefined;
  /** Scaling configuration: Exactly one of `autoscaling_config` or `provisioned_config` must be provided (mutually exclusive). */
  autoscaling_config?: AutoscalingConfig | undefined;
  provisioned_config?:
    | ProvisionedScalingConfig
    | undefined;
  /** The instance type for Ingest Server Group nodes. Optional. Defaults to `m6a.2xlarge` if not specified. Must be a valid instance type (e.g., m5.large, c5.xlarge). */
  node_type?: string | undefined;
}

export interface DeleteIngestServerGroupRequest {
  /** The ID of the Ingest Server Group to delete. */
  id?: string | undefined;
}

export interface DeleteIngestServerGroupResponse {
}

export interface UpdateIngestServerGroupRequest {
  /** The ID of the Ingest Server Group to update. */
  id?:
    | string
    | undefined;
  /** Metadata containing descriptive information and organizational labels for the Ingest Server Group. */
  metadata?: ResourceMetadata | undefined;
  autoscaling_config?: AutoscalingConfig | undefined;
  provisioned_config?:
    | ProvisionedScalingConfig
    | undefined;
  /**
   * The instance type for Ingest Server Group nodes. Optional. Defaults to `m6a.2xlarge` if not specified. Must be a valid instance type (e.g., m5.large, c5.xlarge).
   * Note: Node type updates may take several minutes to complete.
   */
  node_type?: string | undefined;
}

export interface ListIngestServerGroupsRequest {
  /**
   * The name of the workspace to list Ingest Server Groups in.
   * If no workspace is provided, list all Ingest Server Groups across all workspaces
   * the caller has permissions to view.
   */
  workspace?: string | undefined;
}

export interface ListIngestServerGroupsResponse {
  /**
   * List of Ingest Server Groups in the specified workspace (or all workspaces
   * the caller can view if no workspace was requested)
   */
  ingest_server_groups?: IngestServerGroup[] | undefined;
}

export interface GetIngestServerGroupRequest {
  /** The ID of the Ingest Server Group to retrieve. */
  id?: string | undefined;
}

export interface FeatureServerGroup {
  workspace?: string | undefined;
  name?: string | undefined;
  id?: string | undefined;
  metadata?: ResourceMetadata | undefined;
  status?: Status | undefined;
  status_details?: string | undefined;
  created_at?: string | undefined;
  updated_at?: string | undefined;
  autoscaling_config?: AutoscalingConfig | undefined;
  provisioned_config?: ProvisionedScalingConfig | undefined;
  node_type?: string | undefined;
  cache_id?:
    | string
    | undefined;
  /** User requested configuration for the feature server group. */
  pending_config?: FeatureServerGroup_PendingConfig | undefined;
}

/**
 * Fields representing the requested state of the FSG.
 * Will only be set in responses if the requested state differs
 */
export interface FeatureServerGroup_PendingConfig {
  autoscaling_config?: AutoscalingConfig | undefined;
  provisioned_config?: ProvisionedScalingConfig | undefined;
  node_type?: string | undefined;
  cache_id?: string | undefined;
}

export interface CreateFeatureServerGroupRequest {
  workspace?: string | undefined;
  name?: string | undefined;
  metadata?: ResourceMetadata | undefined;
  autoscaling_config?: AutoscalingConfig | undefined;
  provisioned_config?: ProvisionedScalingConfig | undefined;
  node_type?: string | undefined;
  cache_id?: string | undefined;
}

export interface DeleteFeatureServerGroupRequest {
  id?: string | undefined;
}

export interface DeleteFeatureServerGroupResponse {
}

export interface UpdateFeatureServerGroupRequest {
  id?: string | undefined;
  metadata?: ResourceMetadata | undefined;
  autoscaling_config?: AutoscalingConfig | undefined;
  provisioned_config?: ProvisionedScalingConfig | undefined;
  node_type?: string | undefined;
  cache_id?: string | undefined;
}

export interface ListFeatureServerGroupsRequest {
  workspace?: string | undefined;
}

export interface ListFeatureServerGroupsResponse {
  feature_server_groups?: FeatureServerGroup[] | undefined;
}

export interface GetFeatureServerGroupRequest {
  id?: string | undefined;
}

export interface ProvisionedScalingCacheConfig {
  num_shards?: number | undefined;
  num_replicas_per_shard?: number | undefined;
}

export interface FeatureServerCache {
  workspace?: string | undefined;
  name?: string | undefined;
  id?: string | undefined;
  metadata?: ResourceMetadata | undefined;
  status?: Status | undefined;
  status_details?: string | undefined;
  created_at?: string | undefined;
  updated_at?: string | undefined;
  provisioned_config?:
    | ProvisionedScalingCacheConfig
    | undefined;
  /** AWS range format: "ddd:hh24:mi-ddd:hh24:mi" */
  preferred_maintenance_window?:
    | string
    | undefined;
  /** User requested configuration for the feature server cache. */
  pending_config?: FeatureServerCache_PendingConfig | undefined;
}

/**
 * Fields representing the requested state of the FSC.
 * Will only be set in responses if the requested state differs
 */
export interface FeatureServerCache_PendingConfig {
  provisioned_config?: ProvisionedScalingCacheConfig | undefined;
  preferred_maintenance_window?: string | undefined;
}

export interface CreateFeatureServerCacheRequest {
  workspace?: string | undefined;
  name?: string | undefined;
  metadata?: ResourceMetadata | undefined;
  provisioned_config?: ProvisionedScalingCacheConfig | undefined;
  preferred_maintenance_window?: string | undefined;
}

export interface DeleteFeatureServerCacheRequest {
  id?: string | undefined;
}

export interface DeleteFeatureServerCacheResponse {
}

export interface UpdateFeatureServerCacheRequest {
  id?: string | undefined;
  metadata?: ResourceMetadata | undefined;
  provisioned_config?: ProvisionedScalingCacheConfig | undefined;
  preferred_maintenance_window?: string | undefined;
}

export interface ListFeatureServerCachesRequest {
  workspace?: string | undefined;
}

export interface ListFeatureServerCachesResponse {
  feature_server_caches?: FeatureServerCache[] | undefined;
}

export interface GetFeatureServerCacheRequest {
  id?: string | undefined;
}
